import AlreadySignedUpDialog from "@components/AlreadySignedUpDialog"
import Button from "@components/Button"
import Input from "@components/Input"
import Layout from "@components/Layout"
import { trackCompleteRegistrationEvent } from "@helpers/facebookTracking"
import {
  getPasswordFromSessionStorage,
  getUserId,
  removePasswordFromSessionStorage,
} from "@helpers/sessionStorage"
import { signUp } from "@helpers/supabase"
import { validateEmail } from "@helpers/validations"
import * as Sentry from "@sentry/react"
import { setUserId } from "@snowplow/browser-tracker"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

export default function EmailPage() {
  const [email, setEmail] = useState("")
  const [emailValid, setEmailValid] = useState(true)
  const [password, setPassword] = useState("")
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmailValid = validateEmail(email)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (isSubmitting) return
    event.preventDefault()

    if (isEmailValid) {
      setIsSubmitting(true)

      const { error } = await signUp({ email, password })

      if (error) {
        if (error.message === "User already registered") {
          setOpenAlreadySignedUpDialog(true)
        } else {
          Sentry.captureException(error)
        }

        setIsSubmitting(false)

        return
      }

      removePasswordFromSessionStorage()

      const userId = await getUserId()

      if (userId) {
        setUserId(userId)
      }

      await trackCompleteRegistrationEvent()

      setIsSubmitting(false)

      navigate("/sign-up/building-plan")
    } else {
      setEmailValid(false)
    }
  }

  useEffect(() => {
    const passwordFromSessionStorage = getPasswordFromSessionStorage()

    if (passwordFromSessionStorage) {
      setPassword(passwordFromSessionStorage)
    } else {
      navigate("/sign-up/password")
    }
  }, [navigate])

  return (
    <>
      <Layout>
        <div className="shrink-0 space-y-6 text-center">
          <h1 className="custom-heading">{t("pages.signUp.email.heading")}</h1>

          <p
            className={
              emailValid ? "custom-subheading" : "custom-subheading-error"
            }
          >
            {emailValid
              ? t("pages.signUp.email.subheading")
              : t("pages.signUp.email.emailNotValid")}
          </p>
        </div>

        <form
          className="flex grow flex-col justify-between"
          noValidate
          onSubmit={handleSubmit}
        >
          <div className="flex grow flex-col justify-center gap-4">
            <Input
              autoFocus
              description={
                <Trans
                  components={{
                    privacyPolicyLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to={"https://www.perfectday.app/privacy"}
                      />
                    ),
                    termsAndConditionsLink: (
                      <Link
                        className="underline"
                        target="_blank"
                        to={"https://www.perfectday.app/terms"}
                      />
                    ),
                  }}
                  i18nKey="pages.signUp.email.text"
                />
              }
              disabled={isSubmitting}
              invalid={!emailValid}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t("shared.email")}
              type="email"
            />
          </div>

          <div className="shrink-0 text-center">
            <Button
              disabled={email.length < 1 || isSubmitting}
              iconOnly
              type="submit"
              title={t("shared.continue")}
            />
          </div>
        </form>
      </Layout>

      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
